import _ from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import PapaParse from 'papaparse'
import { pdf } from '@react-pdf/renderer';
import {
    AppBar, Toolbar, Button, Link, Paper, Grid, Typography, TextField,
    InputAdornment, Table, TableHead, TableBody, TableRow, TableCell,
    IconButton, Tooltip, CircularProgress,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import EmailIcon from '@material-ui/icons/Email';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import MyDocument, { statementFileName, saveBlob, emailStatement } from 'components/statement'
import { auth } from 'firebase_config';

const NAVY = '#253e89';
const money = num => (Number(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","));

const useStyles = makeStyles(theme => ({
    logo: {
        height: 34,
        marginRight: theme.spacing(2),
    },
    content: {
        maxWidth: 960,
        margin: '0 auto',
        padding: theme.spacing(3, 2, 6),
    },
    dropzone: {
        border: `2px dashed ${theme.palette.grey[400]}`,
        borderRadius: 8,
        padding: theme.spacing(6, 3),
        textAlign: 'center',
        cursor: 'pointer',
        backgroundColor: 'white',
        transition: 'border-color 120ms, background-color 120ms',
    },
    dropzoneActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#eef3fd',
    },
    dropzoneCompact: {
        padding: theme.spacing(1.5, 2),
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    uploadIcon: {
        fontSize: 48,
        color: theme.palette.primary.main,
    },
    stat: {
        padding: theme.spacing(1.5, 2),
    },
    statValue: {
        fontWeight: 700,
        color: NAVY,
    },
    controls: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    tablePaper: {
        marginTop: theme.spacing(2),
        overflowX: 'auto',
    },
    actionsCell: {
        whiteSpace: 'nowrap',
        width: 120,
    },
}));

// QuickBooks Online "A/R Aging Detail" export columns:
// 0 aging-section label (blank on data rows) | 1 Date | 2 Transaction type
// 3 Num | 4 Customer full name | 5 Due date | 6 Amount | 7 Open balance
// Aging is no longer a column — it comes from the due date vs the report's
// "As of" date — and amounts can carry "$" and thousands commas, so
// normalize() appends two computed columns for the statement to use:
// 8 days past due | 9 open balance as a plain number string
const normalize = data => {
    const asOfRow = _.find(data, row => (_.startsWith(row[0], 'As of ')));
    const asOf = asOfRow ? new Date(asOfRow[0].replace('As of ', '')) : new Date();
    const rows = _.filter(data, row => (row[2] === 'Invoice'));
    _.forEach(rows, row => {
        row.push(Math.max(0, Math.round((asOf - new Date(row[5])) / 86400000)));
        row.push((parseFloat(String(row[7]).replace(/[$,]/g, '')) || 0).toFixed(2));
    });
    return { rows, asOf };
};

const UploadZone = ({ onData, compact, fileName, error, loading }) => {
    const classes = useStyles();
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const readFile = file => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = event => onData(PapaParse.parse(event.target.result).data, file.name);
        reader.readAsText(file, 'UTF-8');
    };
    const onDrop = event => {
        event.preventDefault();
        setDragging(false);
        readFile(event.dataTransfer.files[0]);
    };
    const zoneClass = [
        classes.dropzone,
        dragging ? classes.dropzoneActive : '',
        compact ? classes.dropzoneCompact : '',
    ].join(' ');
    return (
        <div
            className={zoneClass}
            onClick={() => inputRef.current.click()}
            onDragOver={event => { event.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
        >
            <input
                ref={inputRef}
                type="file"
                accept=".csv, text/csv"
                style={{ display: 'none' }}
                onChange={event => { readFile(event.target.files[0]); event.target.value = ''; }}
            />
            {compact ?
                <React.Fragment>
                    <Typography variant="body2">
                        <b>{fileName}</b> loaded — click or drop a new export to replace it
                    </Typography>
                    <Button size="small" color="primary" style={{ whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 16 }}>
                        Replace file
                    </Button>
                </React.Fragment>
                :
                <React.Fragment>
                    {loading ? <CircularProgress /> : <CloudUploadIcon className={classes.uploadIcon} />}
                    <Typography variant="h6" gutterBottom>
                        Drop your A/R Aging Detail export here
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        or click to browse — use the CSV export of QuickBooks' "A/R Aging Detail" report
                    </Typography>
                    {error ?
                        <Typography variant="body2" color="error" style={{ marginTop: 8 }}>{error}</Typography>
                        : null}
                </React.Fragment>
            }
        </div>
    );
};

const CustomerRow = ({ customer }) => {
    const classes = useStyles();
    const [busy, setBusy] = useState(null);
    const total = money(customer.total);
    const makeBlob = () => pdf(<MyDocument list={customer.list} customer={customer.name} />).toBlob();
    const download = async () => {
        setBusy('download');
        try {
            saveBlob(await makeBlob(), statementFileName(customer.name));
        } finally {
            setBusy(null);
        }
    };
    const email = async () => {
        setBusy('email');
        try {
            await emailStatement(await makeBlob(), customer.name, total);
        } finally {
            setBusy(null);
        }
    };
    return (
        <TableRow hover>
            <TableCell>{customer.name}</TableCell>
            <TableCell align="right">{customer.list.length}</TableCell>
            <TableCell align="right">${total}</TableCell>
            <TableCell align="right" className={classes.actionsCell}>
                {busy ?
                    <CircularProgress size={22} style={{ margin: 9 }} />
                    :
                    <React.Fragment>
                        <Tooltip title="Download PDF statement">
                            <IconButton size="small" style={{ marginRight: 8 }} aria-label={`Download statement for ${customer.name}`} onClick={download}>
                                <GetAppIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Email statement">
                            <IconButton size="small" aria-label={`Email statement for ${customer.name}`} onClick={email}>
                                <EmailIcon />
                            </IconButton>
                        </Tooltip>
                    </React.Fragment>
                }
            </TableCell>
        </TableRow>
    );
};

const Aging = ({ history }) => {
    const classes = useStyles();
    // one state object so the FileReader callback commits atomically
    // (setState outside React events is unbatched in React 16)
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [amountText, setAmountText] = useState('');

    const loadData = (data, name) => {
        const parsed = normalize(data);
        if (!parsed.rows.length) {
            setError('No invoices found in that file — is it the CSV export of the A/R Aging Detail report?');
            return;
        }
        setError(null);
        setReport({ rows: parsed.rows, asOf: parsed.asOf, fileName: name });
    };

    const minAmount = parseFloat(amountText) || 0;
    const customers = useMemo(() => {
        const kept = _.filter(report && report.rows, row => (parseFloat(row[9]) >= minAmount));
        return _.sortBy(
            _.map(_.groupBy(kept, row => (row[4])), (list, name) => ({
                name,
                list: _.sortBy(list, i => (Date.parse(i[1]))),
                total: _.sumBy(list, i => (Number(i[9]))),
            })),
            customer => (customer.name.toLowerCase()),
        );
    }, [report, minAmount]);
    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        return term ? _.filter(customers, c => (c.name.toLowerCase().includes(term))) : customers;
    }, [customers, search]);

    const signOut = () => auth.signOut().then(() => history.push('/'));

    return (
        <div>
            <AppBar position="static" color="inherit" elevation={1}>
                <Toolbar>
                    <img src={process.env.PUBLIC_URL + '/gp_logo.png'} alt="Gulf-Pro Services" className={classes.logo} />
                    <Typography variant="h6" style={{ flexGrow: 1, color: NAVY }}>
                        A/R Statements
                    </Typography>
                    <Link href="https://youtu.be/lPj0U6s3s-w" target="_blank" rel="noopener" style={{ marginRight: 16 }}>
                        Video guide
                    </Link>
                    <Button startIcon={<ExitToAppIcon />} onClick={signOut}>
                        Sign out
                    </Button>
                </Toolbar>
            </AppBar>
            <div className={classes.content}>
                <UploadZone onData={loadData} compact={!!report} fileName={report ? report.fileName : ''} error={error} />
                {report ?
                    <React.Fragment>
                        <Grid container spacing={2} style={{ marginTop: 8 }}>
                            {[
                                ['Report as of', report.asOf.toLocaleDateString()],
                                ['Customers', String(customers.length)],
                                ['Open invoices', String(_.sumBy(customers, c => (c.list.length)))],
                                ['Total open', '$' + money(_.sumBy(customers, 'total'))],
                            ].map(([label, value]) => (
                                <Grid item xs={6} sm={3} key={label}>
                                    <Paper className={classes.stat}>
                                        <Typography variant="caption" color="textSecondary">{label}</Typography>
                                        <Typography variant="h6" className={classes.statValue}>{value}</Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                        <Grid container spacing={2} className={classes.controls}>
                            <Grid item xs={12} sm={7}>
                                <TextField
                                    value={search}
                                    onChange={event => setSearch(event.target.value)}
                                    placeholder="Search customers"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><SearchIcon color="disabled" /></InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    value={amountText}
                                    onChange={event => setAmountText(event.target.value)}
                                    label="Minimum invoice amount"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    helperText="Invoices below this amount are left off statements"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Paper className={classes.tablePaper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Customer</TableCell>
                                        <TableCell align="right">Open invoices</TableCell>
                                        <TableCell align="right">Total due</TableCell>
                                        <TableCell align="right">Statement</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visible.map(customer => (
                                        <CustomerRow key={customer.name} customer={customer} />
                                    ))}
                                    {visible.length === 0 ?
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" style={{ padding: 24 }}>
                                                <Typography variant="body2" color="textSecondary">
                                                    No customers match the current search and amount filter.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        : null}
                                </TableBody>
                            </Table>
                        </Paper>
                    </React.Fragment>
                    : null}
            </div>
        </div>
    );
};

export default Aging;
